import type {
  CurrentUserWorkbookAccessResponse,
  IBusiness,
} from "@/business/business";
import { isPayrollFeatureEnabled } from "@/business/product-access";
import { useGetCurrentUserWorkbookAccess } from "@/business/query";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { hasFullWorkbookAuthority } from "@/payroll/pay-breakdown/workbook-authority";
import { isAdminOrOwner, isOwner } from "@/user/utils";
import type { PayrollRoster } from "./types";

// Structural user shape so BOTH user sources satisfy it: the auth-layer `IUser`
// (`parentable_type: string`) used on the Grid and the richer `DetailedUser`
// (`parentable_type: "Business" | "Tenant"`) from `useGetMyUser` on the roster
// page. Widening to `string` accepts either without coupling to one source.
interface PayrollRosterUser {
  parentable_type?: string;
  role?: string;
}

export const isSupermanager = (user: PayrollRosterUser | null | undefined) =>
  user?.parentable_type?.toUpperCase() === "TENANT";

export const canSyncPayrollRosterTechnicians = (
  business: IBusiness | null | undefined,
  user: PayrollRosterUser | null | undefined,
) =>
  isSupermanager(user) ||
  (isOwner(user?.role) && hasFullWorkbookAuthority(business, user));

// Matches the API's can_manage_finances? rule for this business-scoped surface:
// business admins/owners and tenant-level users can manage a roster once the
// published AI script declares a roster contract.
export const canManagePayrollRoster = (
  business: IBusiness | null | undefined,
  user: PayrollRosterUser | null | undefined,
) =>
  !!business?.supports_payroll_roster &&
  (user?.parentable_type === "Tenant" || isAdminOrOwner(user?.role));

export const canEditPayrollRoster = (
  business: IBusiness | null | undefined,
  user: PayrollRosterUser | null | undefined,
  access: CurrentUserWorkbookAccessResponse | undefined,
  roster?: PayrollRoster | null,
) =>
  access?.roster_visibility === "full" &&
  canManagePayrollRoster(business, user) &&
  (!roster?.team_scope_active || access.mode === "full");

// Current API access is required even when an older response is cached.
export const usePayrollRosterAccess = (
  businessId: string | null | undefined,
  options?: { enabled?: boolean },
) => {
  const query = useGetCurrentUserWorkbookAccess(businessId, options);
  const enabled = !!businessId && options?.enabled !== false;
  return {
    ...query,
    canReadRoster:
      enabled &&
      query.isSuccess &&
      query.isFetchedAfterMount &&
      (query.data?.roster_visibility === "full" ||
        query.data?.roster_visibility === "teams"),
    isAccessLoading: enabled && !query.isFetchedAfterMount && !query.isError,
  };
};

// Read access comes from assignments evaluated by the API, independently of role.
export const useCanViewPayrollRoster = (
  business: IBusiness | null | undefined,
  user: PayrollRosterUser | null | undefined,
) => {
  const { settings, isLoading } = useBusinessProducts();
  const enabled =
    isPayrollFeatureEnabled(settings, "roster") &&
    !!business?.supports_payroll_roster &&
    !!user;
  const accessQuery = usePayrollRosterAccess(business?.id, { enabled });
  return {
    accessQuery,
    canViewRoster: accessQuery.canReadRoster,
    isLoading: isLoading || accessQuery.isAccessLoading,
  };
};
